/**
 * Address Synchronizer module.
 *
 * Handles synchronization between Company Address, Billing Address, and Attendee Information.
 */

const getStorageKey = ( type: 'address' | 'contact', index: number ) => {
	const base = type === 'address' ? 'crsc_previous_attendee_info' : 'crsc_previous_contact_info';
	return `${base}${index + 1}`;
};

const getFieldKey = ( name: string ): string => {
	const match = name.match( /\[([^\]]+)\]$/ );
	return match ? match[1] : name;
};

const getFieldType = ( key: string ): 'address' | 'contact' | null => {
	const addressFields = [
		'street_address', 'zip', 'city', 'state',
		'material_shipping_street_address', 'material_shipping_city',
		'material_shipping_state', 'material_shipping_zip'
	];
	if ( addressFields.some( r => key.includes( r ) ) ) return 'address';

	const contactFields = [
		'first_name', 'middle', 'last_name', 'suffix', 'email', 'phone', 'mobile_phone', 'work_phone'
	];
	if ( contactFields.some( r => key.includes( r ) ) ) return 'contact';

	return null;
};

const saveAttendeeFieldToStorage = ( key: string, value: string, type: 'address' | 'contact', index: number ) => {
	const storageKey = getStorageKey( type, index );
	const stored = localStorage.getItem( storageKey );
	const data: Record<string, string> = stored ? JSON.parse( stored ) : {};

	data[key] = value;
	localStorage.setItem( storageKey, JSON.stringify( data ) );
};

const getAttendeeFieldFromStorage = ( key: string, type: 'address' | 'contact', index: number ): string | null => {
	const storageKey = getStorageKey( type, index );
	const stored = localStorage.getItem( storageKey );
	if ( ! stored ) return null;

	const data: Record<string, string> = JSON.parse( stored );
	return data[key] || null;
};


/**
 * Sets an input value in a way that React 16+ sees it.
 * This bypasses React's value property override.
 *
 * @see https://stackoverflow.com/a/64877531
 */
const setNativeValue = ( element: HTMLElement, value: string ) => {
	const valueSetter = Object.getOwnPropertyDescriptor( element, 'value' )?.set;
	const prototype = Object.getPrototypeOf( element );
	const prototypeValueSetter = Object.getOwnPropertyDescriptor( prototype, 'value' )?.set;

	if ( prototypeValueSetter && valueSetter !== prototypeValueSetter ) {
		prototypeValueSetter.call( element, value );
	} else if ( valueSetter ) {
		valueSetter.call( element, value );
	} else {
		( element as HTMLInputElement ).value = value;
	}

	element.dispatchEvent( new Event( 'input', { bubbles: true } ) );
	element.dispatchEvent( new Event( 'change', { bubbles: true } ) );
};

/**
 * Updates a field in the WooCommerce Blocks data store if available.
 * This is the official "API way" to update fields in the Checkout Block.
 */
const updateBlocksStore = ( name: string, value: string ) => {
	const wpData = ( window as any ).wp?.data;
	if ( ! wpData ) return;

	// Update core billing fields
	if ( name.startsWith( 'billing-' ) || name.startsWith( 'billing_' ) ) {
		const fieldKey = name.replace( /^billing[-_]/, '' ).replace( '-', '_' );
		const cartStore = wpData.dispatch( 'wc/store/cart' );
		if ( cartStore ) {
			cartStore.setBillingAddress( { [ fieldKey ]: value } );
		}
	}

	// Update additional fields (custom fields)
	if ( name.includes( 'crsc/' ) ) {
		const checkoutStore = wpData.dispatch( 'wc/store/checkout' );
		if ( checkoutStore ) {
			const currentData = wpData.select( 'wc/store/checkout' )?.getExtensionData();
			const additionalFields = currentData?.[ 'woocommerce/additional-fields' ] || {};
			const fieldKey = name.replace( /^(contact|address)_/, '' );

			checkoutStore.setExtensionData( 'woocommerce/additional-fields', {
				...additionalFields,
				[ fieldKey ]: value
			} );
		}
	}
};

export const initAddressSynchronizer = () => {
	const isCheckout = document.body.classList.contains( 'woocommerce-checkout' );
	const isEvent = document.body.classList.contains( 'single-tribe_events' );
	const isEditAttendee = document.body.classList.contains( 'page-tribe-attendee-registration' );

	if ( isCheckout ) {
		handleCheckoutPage();
	}

	if ( isEvent || isCheckout || isEditAttendee ) {
		handleAttendeeSync();
	}
};

/**
 * Handles logic specific to the WooCommerce Checkout page.
 */
const handleCheckoutPage = () => {
	const observer = new MutationObserver( () => {
		// Move billing-address-same checkbox to the top of its parent
		const billingSameWrapper = document.querySelector( '.wc-block-components-address-form__crsc-billing-address-same' );
		if ( billingSameWrapper && billingSameWrapper.parentElement ) {
			const parent = billingSameWrapper.parentElement;
			if ( parent.firstChild !== billingSameWrapper ) {
				parent.insertBefore( billingSameWrapper, parent.firstChild );
			}
		}

		// Handle "Same as Company Address" for Billing
		const billingSameCheckbox = document.querySelector( '[name="billing_crsc/billing-address-same"]' ) as HTMLInputElement;
		if ( billingSameCheckbox && ! billingSameCheckbox.dataset.listenerAdded ) {
			billingSameCheckbox.addEventListener( 'change', ( e ) => {
				if ( ( e.target as HTMLInputElement ).checked ) {
					copyCompanyToBilling();
				}
			} );
			billingSameCheckbox.dataset.listenerAdded = 'true';
		}

		// Handle "Same as Attendee Address" for Company
		const companySameCheckbox = document.querySelector( '[name="contact_crsc/company-address-same"]' ) as HTMLInputElement;
		if ( companySameCheckbox && ! companySameCheckbox.dataset.listenerAdded ) {
			companySameCheckbox.addEventListener( 'change', ( e ) => {
				if ( ( e.target as HTMLInputElement ).checked ) {
					copyAttendeeToCompany();
				}
			} );
			companySameCheckbox.dataset.listenerAdded = 'true';
		}
	} );

	observer.observe( document.body, { childList: true, subtree: true } );
};

/**
 * Copies values from custom company fields to billing fields.
 */
const copyCompanyToBilling = () => {
	const fieldMap: Record<string, string> = {
		'contact_crsc/billing-company-address-1': 'billing-address_1',
		'contact_crsc/billing-company-address-2': 'billing-address_2',
		'contact_crsc/billing-company-city': 'billing-city',
		'contact_crsc/billing-company-state': 'billing-state',
		'contact_crsc/billing-company-postcode': 'billing-postcode',
	};

	Object.keys( fieldMap ).forEach( ( companyFieldName ) => {
		const billingFieldId = fieldMap[companyFieldName];
		const companyInput = document.querySelector( `[name="${companyFieldName}"]` ) as HTMLInputElement | HTMLSelectElement;
		const billingInput = document.getElementById( billingFieldId ) as HTMLInputElement | HTMLSelectElement;

		if ( companyInput && billingInput ) {
			setNativeValue( billingInput, companyInput.value );
			updateBlocksStore( billingFieldId, companyInput.value );
		}
	} );
};

/**
 * Copies values from the first attendee to company fields.
 */
const copyAttendeeToCompany = () => {
	const fieldMap: Record<string, string> = {
		'street_address': 'contact_crsc/billing-company-address-1',
		'city': 'contact_crsc/billing-company-city',
		'state': 'contact_crsc/billing-company-state',
		'zip': 'contact_crsc/billing-company-postcode',
	};

	Object.keys( fieldMap ).forEach( ( attendeeFieldKey ) => {
		const companyFieldName = fieldMap[attendeeFieldKey];
		const storedValue = getAttendeeFieldFromStorage( attendeeFieldKey, 'address', 0 );
		console.log( 'storedValue', attendeeFieldKey, storedValue, companyFieldName );
		const companyInput = document.querySelector( `[name="${companyFieldName}"]` ) as HTMLInputElement | HTMLSelectElement;

		if ( storedValue && companyInput ) {
			setNativeValue( companyInput, storedValue );
			updateBlocksStore( companyFieldName, storedValue );
		}
	} );
};

/**
 * Handles attendee information synchronization and local storage.
 */
const handleAttendeeSync = () => {
	const syncAll = () => {
		const attendeeContainers = document.querySelectorAll( '.tribe-tickets__attendee-tickets' );
		const attendeeItems = document.querySelectorAll( '.tribe-tickets__attendee-tickets-item' );

		if ( attendeeContainers.length > 0 ) {
			setupAttendeeListeners( attendeeContainers );
			setupAttendeeCopyCheckboxes( attendeeContainers );
		} else if ( attendeeItems.length > 0 ) {
			// Fallback if the main container class is missing
			attendeeItems.forEach( ( item, index ) => {
				processAttendeeContainer( item, index );
			} );
		}

		// Handle material shipping checkboxes for all items/containers found
		const allItems = document.querySelectorAll( '.tribe-tickets__attendee-tickets-item' );
		if ( allItems.length > 0 ) {
			setupMaterialShippingCheckboxes( allItems );
		} else if ( attendeeContainers.length > 0 ) {
			setupMaterialShippingCheckboxes( attendeeContainers );
		}
	};

	const observer = new MutationObserver( syncAll );

	observer.observe( document.body, { childList: true, subtree: true } );

	const setupAttendeeListeners = ( containers: NodeListOf<Element> ) => {
		containers.forEach( ( container ) => {
			const items = container.querySelectorAll( '.tribe-tickets__attendee-tickets-item' );
			if ( items.length === 0 ) {
				// Fallback if items not used
				processAttendeeContainer( container, 0 );
			} else {
				items.forEach( ( item, index ) => {
					processAttendeeContainer( item, index );
				} );
			}
		} );
	};

	const processAttendeeContainer = ( container: Element, attendeeIndex: number ) => {
		const inputs = container.querySelectorAll( 'input, select' );

		inputs.forEach( ( input ) => {
			const name = ( input as HTMLInputElement | HTMLSelectElement ).name;
			if ( ! name ) return;

			const fieldKey = getFieldKey( name );
			const fieldType = getFieldType( fieldKey );
			if ( ! fieldType ) return;

			// Listen for changes to save to localStorage
			if ( ! ( input as any )._hasAttendeeListener ) {
				input.addEventListener( 'change', () => {
					const value = ( input as HTMLInputElement | HTMLSelectElement ).value;
					saveAttendeeFieldToStorage( fieldKey, value, fieldType, attendeeIndex );
				} );
				( input as any )._hasAttendeeListener = true;
			}

			// Prefill if empty and we have data in localStorage (only once per element)
			if ( ! ( input as any )._hasBeenPrefilled ) {
				if ( ! ( input as HTMLInputElement | HTMLSelectElement ).value ) {
					const storedValue = getAttendeeFieldFromStorage( fieldKey, fieldType, attendeeIndex );

					if ( storedValue ) {
						setNativeValue( input as HTMLInputElement, storedValue );
					}
				}
				( input as any )._hasBeenPrefilled = true;
			}
		} );
	};

	const setupAttendeeCopyCheckboxes = ( containers: NodeListOf<Element> ) => {
		containers.forEach( ( container ) => {
			const items = container.querySelectorAll( '.tribe-tickets__attendee-tickets-item' );
			items.forEach( ( item, index ) => {
				if ( index === 0 ) return;

				const header = item.querySelector( '.tribe-tickets__attendee-tickets-item-header' );
				if ( ! header || item.querySelector( '.crsc-copy-attendee-address-container' ) ) return;

				const containerDiv = document.createElement( 'div' );
				containerDiv.className = 'crsc-copy-attendee-address-container';
				containerDiv.style.margin = '10px 0';

				const checkbox = document.createElement( 'input' );
				checkbox.type = 'checkbox';
				checkbox.className = 'crsc-copy-attendee-address-checkbox';
				checkbox.id = `crsc-copy-attendee-address-${Math.random().toString( 36 ).slice( 2, 9 )}`;
				checkbox.style.marginRight = '5px';

				const label = document.createElement( 'label' );
				label.htmlFor = checkbox.id;
				label.textContent = `Copy Attendee 1 Address to Attendee ${index + 1}`;

				containerDiv.appendChild( checkbox );
				containerDiv.appendChild( label );

				header.parentNode?.insertBefore( containerDiv, header.nextSibling );

				checkbox.addEventListener( 'change', ( e ) => {
					if ( ( e.target as HTMLInputElement ).checked ) {
						copyAttendeeData( container, 0, item );
					}
				} );
			} );
		} );
	};

	const copyAttendeeData = ( container: Element, sourceIndex: number, targetItem: Element ) => {
		const excludedFields = [ 'first_name', 'middle', 'last_name', 'suffix', 'email', 'phone', 'mobile_phone' ];
		const items = container.querySelectorAll( '.tribe-tickets__attendee-tickets-item' );
		const sourceItem = items[sourceIndex];
		if ( ! sourceItem ) return;

		const sourceInputs = sourceItem.querySelectorAll( 'input, select' );
		const targetInputs = targetItem.querySelectorAll( 'input, select' );

		sourceInputs.forEach( ( sInput ) => {
			const sName = ( sInput as HTMLInputElement ).name;
			if ( ! sName ) return;
			const fieldKey = getFieldKey( sName );

			if ( excludedFields.includes( fieldKey ) ) return;

			const fType = getFieldType( fieldKey );
			if ( fType !== 'address' && fType !== 'contact' ) return;

			const tInput = Array.from( targetInputs ).find( i => getFieldKey( ( i as HTMLInputElement ).name ) === fieldKey ) as HTMLInputElement;
			if ( tInput ) {
				setNativeValue( tInput, ( sInput as HTMLInputElement ).value );
			}
		} );
	};

	const setupMaterialShippingCheckboxes = ( items: NodeListOf<Element> | Element[] ) => {
		items.forEach( ( item ) => {
			const materialInput = item.querySelector( '[name*="[material_shipping_street_address]"]' ) as HTMLInputElement;
			if ( ! materialInput || item.querySelector( '.crsc-material-shipping-copy-container' ) ) return;

			const fieldWrapper = materialInput.closest( '.tribe-tickets__form-field' );
			if ( ! fieldWrapper ) return;

			const containerDiv = document.createElement( 'div' );
			containerDiv.className = 'crsc-material-shipping-copy-container';
			containerDiv.style.margin = '10px 0';

			const checkbox = document.createElement( 'input' );
			checkbox.type = 'checkbox';
			checkbox.className = 'crsc-material-shipping-copy-checkbox';
			checkbox.id = `crsc-material-shipping-copy-${Math.random().toString( 36 ).slice( 2, 9 )}`;
			checkbox.style.marginRight = '5px';

			const label = document.createElement( 'label' );
			label.htmlFor = checkbox.id;
			label.textContent = 'Material Shipping Address same as Mailing Address';

			containerDiv.appendChild( checkbox );
			containerDiv.appendChild( label );

			fieldWrapper.parentNode?.insertBefore( containerDiv, fieldWrapper );

			checkbox.addEventListener( 'change', ( e ) => {
				if ( ( e.target as HTMLInputElement ).checked ) {
					copyMailingToMaterial( item );
				}
			} );
		} );
	};

	const copyMailingToMaterial = ( item: Element ) => {
		const map: Record<string, string> = {
			'street_address': 'material_shipping_street_address',
			'city': 'material_shipping_city',
			'state': 'material_shipping_state',
			'zip': 'material_shipping_zip'
		};

		const allInputs = Array.from( item.querySelectorAll( 'input, select' ) ) as ( HTMLInputElement | HTMLSelectElement )[];

		Object.keys( map ).forEach( ( sourceKey ) => {
			const targetKey = map[sourceKey];
			const sourceInput = allInputs.find( i => getFieldKey( i.name ) === sourceKey );
			const targetInput = allInputs.find( i => getFieldKey( i.name ) === targetKey );

			if ( sourceInput && targetInput ) {
				setNativeValue( targetInput, sourceInput.value );
			}
		} );
	};

	// Initial call
	syncAll();
};
